<template>
  <el-dialog
    v-model="dialogVisible"
    title="重置密码"
    width="400px"
    :close-on-click-modal="false"
    destroy-on-close
  >
    <el-steps :active="currentStep" simple class="reset-steps">
      <el-step title="验证手机号" />
      <el-step title="设置新密码" />
    </el-steps>

    <el-form
      v-if="currentStep === 0"
      ref="step1Ref"
      :model="step1Form"
      :rules="step1Rules"
      class="reset-form"
    >
      <el-form-item prop="phone">
        <el-input
          v-model="step1Form.phone"
          placeholder="请输入手机号"
          size="large"
          :prefix-icon="Phone"
          maxlength="11"
        />
      </el-form-item>

      <el-form-item prop="captchaCode">
        <div class="captcha-row">
          <el-input
            v-model="step1Form.captchaCode"
            placeholder="请输入图形验证码"
            size="large"
            :prefix-icon="CircleCheck"
            maxlength="6"
            class="captcha-input"
          />
          <div
            class="captcha-image"
            :class="{ loading: captchaLoading }"
            @click="refreshCaptcha"
          >
            <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
            <el-icon v-else><Loading /></el-icon>
          </div>
        </div>
      </el-form-item>

      <el-form-item prop="smsCode">
        <div class="sms-code-row">
          <el-input
            v-model="step1Form.smsCode"
            placeholder="请输入短信验证码"
            size="large"
            :prefix-icon="Message"
            maxlength="6"
            class="sms-input"
          />
          <el-button
            size="large"
            :disabled="countdown > 0 || smsLoading"
            :loading="smsLoading"
            class="send-btn"
            @click="sendSmsCode"
          >
            {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
          </el-button>
        </div>
      </el-form-item>

      <el-button
        type="primary"
        size="large"
        class="submit-btn"
        @click="verifyPhone"
      >
        下一步
      </el-button>
    </el-form>

    <el-form
      v-else
      ref="step2Ref"
      :model="step2Form"
      :rules="step2Rules"
      class="reset-form"
    >
      <el-form-item prop="password">
        <el-input
          v-model="step2Form.password"
          type="password"
          placeholder="请设置新密码（6-20位）"
          size="large"
          :prefix-icon="Lock"
          show-password
        />
      </el-form-item>

      <el-form-item prop="confirmPassword">
        <el-input
          v-model="step2Form.confirmPassword"
          type="password"
          placeholder="请确认新密码"
          size="large"
          :prefix-icon="Lock"
          show-password
        />
      </el-form-item>

      <div class="form-actions">
        <el-button size="large" @click="currentStep = 0">上一步</el-button>
        <el-button
          type="primary"
          size="large"
          :loading="submitting"
          @click="submitReset"
        >
          确认重置
        </el-button>
      </div>
    </el-form>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { Phone, Message, Lock, CircleCheck, Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import { sendResetSmsCode, verifyResetCode, resetPassword } from '@/api/auth'
import { useCaptcha } from '@/composables/useCaptcha'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  success: []
}>()

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

const currentStep = ref(0)
const smsLoading = ref(false)
const submitting = ref(false)
const countdown = ref(0)
let countdownTimer: number | null = null

const { captchaLoading, captchaImage, captchaKey, refreshCaptcha } = useCaptcha()

// dialog 打开时才请求验证码，避免组件挂载时就发请求
watch(() => props.visible, (visible) => {
  if (visible) {
    refreshCaptcha()
  }
})

const step1Ref = ref<FormInstance>()
const step2Ref = ref<FormInstance>()

const step1Form = ref({
  phone: '',
  captchaCode: '',
  smsCode: '',
})

const step2Form = ref({
  password: '',
  confirmPassword: '',
})

const step1Rules: FormRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' },
  ],
  smsCode: [
    { required: true, message: '请输入短信验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'blur' },
  ],
}

const validateConfirmPassword = (_rule: any, value: string, callback: any) => {
  if (value !== step2Form.value.password) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const step2Rules: FormRules = {
  password: [
    { required: true, message: '请设置新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度6-20位', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' },
  ],
}

const startCountdown = () => {
  countdown.value = 60
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0 && countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }, 1000)
}

const sendSmsCode = async () => {
  const phoneValid = await step1Ref.value?.validateField('phone').catch(() => false)
  const captchaValid = await step1Ref.value?.validateField('captchaCode').catch(() => false)
  if (!phoneValid || !captchaValid) return

  smsLoading.value = true
  try {
    await sendResetSmsCode(step1Form.value.phone, captchaKey.value, step1Form.value.captchaCode)
    ElMessage.success('验证码已发送')
    startCountdown()
  } catch {
    refreshCaptcha()
    step1Form.value.captchaCode = ''
  } finally {
    smsLoading.value = false
  }
}

const verifyPhone = async () => {
  if (!step1Ref.value) return

  await step1Ref.value.validate(async (valid) => {
    if (!valid) return

    try {
      await verifyResetCode({
        phone: step1Form.value.phone,
        smsCode: step1Form.value.smsCode,
      })
      currentStep.value = 1
    } catch (error) {
      // 错误已在拦截器处理
    }
  })
}

const submitReset = async () => {
  if (!step2Ref.value) return

  await step2Ref.value.validate(async (valid) => {
    if (!valid) return

    submitting.value = true
    try {
      await resetPassword({
        phone: step1Form.value.phone,
        smsCode: step1Form.value.smsCode,
        newPassword: step2Form.value.password,
      })
      emit('success')
      dialogVisible.value = false
      resetForm()
    } catch (error) {
      // 错误已在拦截器处理
    } finally {
      submitting.value = false
    }
  })
}

const resetForm = () => {
  currentStep.value = 0
  step1Form.value = { phone: '', captchaCode: '', smsCode: '' }
  step2Form.value = { password: '', confirmPassword: '' }
}

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped lang="scss">
.reset-steps {
  margin-bottom: 24px;
}

.reset-form {
  :deep(.el-input__inner) {
    height: 44px;
  }
}

.sms-code-row {
  display: flex;
  gap: 12px;

  .sms-input {
    flex: 1;
  }

  .send-btn {
    width: 120px;
    height: 44px;
    padding: 0;
  }
}

.captcha-row {
  display: flex;
  gap: 12px;

  .captcha-input {
    flex: 1;
  }

  .captcha-image {
    width: 120px;
    height: 44px;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.loading {
      opacity: 0.6;
    }
  }
}

.submit-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
}

.form-actions {
  display: flex;
  gap: 12px;

  .el-button {
    flex: 1;
    height: 44px;
    font-size: 16px;
  }
}
</style>
